import React from 'react';
import { createApp } from '@backstage/frontend-defaults';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { navModule } from './modules/nav';
import { homeModule } from './modules/home';
import { costInsights } from './modules/cost';
import { clusterAccessModule } from './modules/clusterAccess';
import argoCdPlugin from '@roadiehq/backstage-plugin-argo-cd/alpha';
import { EntityArgoCDOverviewCard } from '@roadiehq/backstage-plugin-argo-cd';
import {
  createFrontendModule,
  ApiBlueprint,
  createApiRef,
  configApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/frontend-plugin-api';
import { ThemeBlueprint, SignInPageBlueprint } from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import {
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { UnifiedThemeProvider } from '@backstage/theme';
import { tallenceLightTheme } from './themes/tallenceTheme';

// Generic OIDC auth API. The identity provider is configured in app-config, so swapping
// it is a config change; keep provider names out of the UI strings below.
const oidcAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({
  id: 'auth.oidc',
});

const oidcAuthApi = ApiBlueprint.make({
  name: 'oidc',
  params: defineParams =>
    defineParams({
      api: oidcAuthApiRef,
      deps: {
        configApi: configApiRef,
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
      },
      factory: ({ configApi, discoveryApi, oauthRequestApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'oidc',
            title: 'Tallence SSO',
            icon: () => null,
          },
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: ['openid', 'profile', 'email'],
          popupOptions: { size: { width: 450, height: 700 } },
        }),
    }),
});

const tallenceThemeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ThemeBlueprint.make({
      name: 'tallence-light',
      params: {
        theme: {
          id: 'tallence-light',
          title: 'Tallence Light',
          variant: 'light' as const,
          Provider: ({ children }: { children: React.ReactNode }) => (
            <UnifiedThemeProvider theme={tallenceLightTheme}>
              {children}
            </UnifiedThemeProvider>
          ),
        },
      },
    }),
  ],
});

const signInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    oidcAuthApi,
    SignInPageBlueprint.make({
      params: {
        loader: async () => props =>
          (
            <SignInPage
              {...props}
              auto
              provider={{
                id: 'oidc',
                title: 'Tallence SSO',
                message: 'Sign in with your Tallence account. Needs a platform group.',
                apiRef: oidcAuthApiRef,
              }}
            />
          ),
      },
    }),
  ],
});

const argoCdPluginConfigured = argoCdPlugin.withOverrides({
  extensions: [
    argoCdPlugin.getExtension('entity-card:argocd/overviewCard').override({
      params: {
        loader: async () => <EntityArgoCDOverviewCard title="App Status" />,
      },
    }),
  ],
});

export default createApp({
  features: [catalogPlugin, navModule, tallenceThemeModule, signInModule, argoCdPluginConfigured, homeModule, costInsights, clusterAccessModule],
});
